import styled from '@emotion/styled';
import type { PropsWithChildren, ReactNode } from 'react';
import { forwardRef } from 'react';

import type { BoxProps } from '../box/Box';
import { Box } from '../box/Box';
import { themeStyles, themeVars } from '../theme/theme';

export type TagProps = BoxProps & {
  withRemoveButton?: boolean;
  onRemove?: () => void;
  disabled?: boolean;
  leftSection?: ReactNode;
  variant?: TagVariant;
  size?: TagSize;
};

export const tagVariantsList = [
  'white',
  'neutral',
  'warm',
  'titan',
  'neon',
  'info',
  'notice',
  'error',
  'success',
  'magenta',
  'blue',
  'orange',
  'teal',
  'pink',
  'purple',
  'cyan',
  'violet',
  'celery',
  'yellow',
  'burgundy',
] as const;

export type TagVariant = (typeof tagVariantsList)[number];

export type TagSize = 'sm' | 'xs';

const Component = forwardRef<HTMLDivElement, PropsWithChildren<TagProps>>(
  (
    {
      children,
      variant = 'neutral',
      size = 'sm',
      disabled,
      leftSection,
      withRemoveButton,
      onRemove,
      ...props
    },
    ref,
  ) => {
    return (
      <Root
        tabIndex={0}
        ref={ref}
        data-variant={variant}
        data-disabled={disabled}
        data-size={size}
        data-with-remove={withRemoveButton}
        data-with-left={!!leftSection}
        {...props}
      >
        {leftSection && <LeftSection data-size={size}>{leftSection}</LeftSection>}
        <Label data-size={size}>{children}</Label>
        {withRemoveButton && (
          <Remove data-size={size} onClick={onRemove}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M10.8284 5.17154L5.17155 10.8284M10.8284 10.8284L5.17155 5.17151"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Remove>
        )}
      </Root>
    );
  },
);

const Root = styled(Box)`
  display: inline-flex;
  align-items: center;
  width: ${(p) => p.w || 'auto'};
  border-radius: 99px;
  user-select: none;
  outline: none;
  transition: all 0.2s ease;

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${themeVars.colors.white},
      0 0 0 4px ${themeVars.colors.info600};
  }

  &[data-size='sm'] {
    padding: 0px 16px;
    height: 32px;
  }

  &[data-size='xs'] {
    padding: 0px 8px;
    height: 24px;
  }

  &[data-with-left='true'] {
    padding-left: 0;
  }

  &[data-with-remove='true'] {
    padding-right: 0;
  }

  &[data-variant='warm'] {
    background: ${themeVars.colors.warmGrey250};
    color: ${themeVars.colors.warmGrey600};
  }

  &[data-variant='white'] {
    color: ${themeVars.colors.coldGrey600};
    border: 1px solid ${themeVars.colors.titan100};
    background: ${themeVars.colors.white};
  }

  &[data-variant='neutral'] {
    background: ${themeVars.colors.coldGrey250};
    color: ${themeVars.colors.coldGrey700};
  }

  &[data-variant='titan'] {
    background: ${themeVars.colors.titan250};
    color: ${themeVars.colors.titan700};
  }

  &[data-variant='neon'] {
    background: ${themeVars.colors.neonFrontRow200};
    color: ${themeVars.colors.neonFrontRow700};
  }

  &[data-variant='info'] {
    background: ${themeVars.colors.info200};
    color: ${themeVars.colors.info700};
  }

  &[data-variant='notice'] {
    background: ${themeVars.colors.notice200};
    color: ${themeVars.colors.notice700};
  }

  &[data-variant='error'] {
    background: ${themeVars.colors.error200};
    color: ${themeVars.colors.error700};
  }

  &[data-variant='success'] {
    background: ${themeVars.colors.success200};
    color: ${themeVars.colors.success700};
  }

  &[data-variant='magenta'] {
    background: ${themeVars.colors.magenta200};
    color: ${themeVars.colors.magenta700};
  }

  &[data-variant='blue'] {
    background: ${themeVars.colors.blue200};
    color: ${themeVars.colors.blue700};
  }

  &[data-variant='orange'] {
    background: ${themeVars.colors.orange200};
    color: ${themeVars.colors.orange700};
  }

  &[data-variant='teal'] {
    background: ${themeVars.colors.teal200};
    color: ${themeVars.colors.teal700};
  }

  &[data-variant='pink'] {
    background: ${themeVars.colors.pink200};
    color: ${themeVars.colors.pink700};
  }

  &[data-variant='purple'] {
    background: ${themeVars.colors.purple200};
    color: ${themeVars.colors.purple700};
  }

  &[data-variant='cyan'] {
    background: ${themeVars.colors.cyan200};
    color: ${themeVars.colors.cyan700};
  }

  &[data-variant='violet'] {
    background: ${themeVars.colors.violet200};
    color: ${themeVars.colors.violet700};
  }

  &[data-variant='celery'] {
    background: ${themeVars.colors.celery200};
    color: ${themeVars.colors.celery700};
  }

  &[data-variant='yellow'] {
    background: ${themeVars.colors.yellow200};
    color: ${themeVars.colors.yellow700};
  }

  &[data-variant='burgundy'] {
    background: ${themeVars.colors.burgundy200};
    color: ${themeVars.colors.burgundy700};
  }

  &[data-disabled='true'] {
    background: ${themeVars.colors.titan100}16;
    color: ${themeVars.colors.coldGrey900}32;
  }

  &[data-size='sm'] {
    ${themeStyles.text.telka.body.lg}
  }

  &[data-size='xs'] {
    ${themeStyles.text.telka.body.xs}
  }
`;

const Label = styled.div``;

const LeftSection = styled.div`
  display: flex;
  align-items: center;

  &[data-size='sm'] {
    padding-left: 8px;
    font-size: 16px;
    margin-right: 8px;
  }

  &[data-size='xs'] {
    padding-left: 4px;
    font-size: 14px;
    margin-right: 4px;
  }
`;

const Remove = styled.button`
  border: none;
  background: none;
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  transition: all 0.2s ease;
  color: currentColor;
  border-radius: 99px;

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${themeVars.colors.white},
      0 0 0 4px ${themeVars.colors.info600};
  }

  &:active {
    transform: translateY(2px);
  }

  &[data-size='sm'] {
    width: 32px;
    font-size: 20px;
  }

  &[data-size='xs'] {
    width: 24px;
    font-size: 16px;
  }
`;

export const Tag = Object.assign(Component, {
  Root,
  Label,
  LeftSection,
  Remove,
});
